/**
 * @file This file contains the Grid class, which manages the state and logic
 * of the game board itself, including cell manipulation and pathfinding.
 * @module Grid
 */

export class Grid {
    /**
     * @param {number} size - The TOTAL size of the grid including buffers.
     *                      (e.g., for a 7x7 playable area with a buffer of 2, the total size would be 11).
     * @param {number} [bufferSize=0] - The size of the non-playable buffer zone around the grid.
     */
    constructor(size, bufferSize = 0) {
        /**
         * A reference to the grid's DOM element, set by the UIManager.
         * @type {HTMLElement|null}
         */
        this.domElement = null;
        /**
         * The total size of the grid (e.g., 11 for an 7x7 grid (2 buffer)).
         * @type {number}
         */
        this.size = size;
        /**
         * The size of the non-playable buffer zone around the grid.
         * @type {number}
         */
        this.bufferSize = bufferSize;
        /**
         * The size of the playable area of the grid.
         * @type {number}
         */
        this.playableSize = size - (2 * bufferSize);
        /**
         * The 2D array representing the grid data.
         * @type {Array<Array<object|null>>}
         */
        this.data = [];
        /**
         * A Set of string-formatted coordinates ('r,c') of empty playable cells for O(1) lookups.
         * @type {Set<string>}
         */
        this.emptyCells = new Set();

        for (let i = 0; i < size; i++) {
            this.data[i] = Array(size).fill(null);
            for (let j = 0; j < size; j++) {
                if (this.isPlayable(i, j)) {
                    this.emptyCells.add(`${i},${j}`);
                }
            }
        }
    }

    /**
     * Checks if a given cell is within the playable area of the grid.
     * @param {number} r - The row index.
     * @param {number} c - The column index.
     * @returns {boolean} True if the cell is playable, false otherwise.
     */
    isPlayable(r, c) {
        return r >= this.bufferSize && r < this.size - this.bufferSize &&
               c >= this.bufferSize && c < this.size - this.bufferSize;
    }
 /**
     * Gets the total number of playable cells on the grid.
     * @returns {number} The count of playable cells.
     */
    getPlayableCellCount() {
        return this.playableSize * this.playableSize;
    }

    /**
     * Gets the item at a specific cell on the grid.
     * @param {number} r - The row index.
     * @param {number} c - The column index.
     * @returns {object|null|undefined} The item at the cell, null if empty, or undefined if out of bounds.
     */
    get(r, c) {
        if (r < 0 || r >= this.size || c < 0 || c >= this.size) {
            return undefined; // Out of bounds
        }
        return this.data[r][c];
    }

    /**
     * Sets the value of a cell on the grid.
     * Updates the `emptyCells` set accordingly.
     * @param {number} r - The row index.
     * @param {number} c - The column index.
     * @param {object|null} value - The item to place in the cell, or null to clear it.
     */
    set(r, c, value) {
        // Previous value at this cell
        const wasEmpty = this.get(r, c) === null;

        this.data[r][c] = value;

        // Update the set of empty cells
        const posKey = `${r},${c}`;
        if (this.isPlayable(r, c)) {
            if (value === null && !wasEmpty) {
                this.emptyCells.add(posKey);
            } else if (value !== null && wasEmpty) {
                this.emptyCells.delete(posKey);
            }
        }
    }

    /**
     * Finds all empty playable cells on the grid.
     * @returns {Array<{r: number, c: number}>} An array of coordinates of empty cells.
     */
    findEmptyCells() {
        // The original implementation iterated the whole grid.
        // This is now just a conversion from a Set to an array.
        return Array.from(this.emptyCells).map(pos => {
            const [r, c] = pos.split(',').map(Number);
            return { r, c };
        });
    }

    /**
     * Gets a random empty playable cell from the grid.
     * @returns {{r: number, c: number}|null} The coordinates of a random empty cell, or null if no empty cells exist.
     */
    getRandomEmptyCell() {
        if (this.emptyCells.size === 0) return null;

        const emptyCellArray = Array.from(this.emptyCells);
        const randomPosKey = emptyCellArray[Math.floor(Math.random() * emptyCellArray.length)];
        const [r, c] = randomPosKey.split(',').map(Number);
        return { r, c };
    }

    /**
     * Gets a random playable cell from the grid, which may or may not be empty.
     * @returns {{r: number, c: number}} The coordinates of a random playable cell.
     */
    getRandomPlayableCell() {
        const r = Math.floor(Math.random() * this.playableSize) + this.bufferSize;
        const c = Math.floor(Math.random() * this.playableSize) + this.bufferSize;
        return { r, c };
    }


    /**
     * Gets a specified number of random playable cells, which may or may not be empty.
     * @param {number} numberOfCells - The number of random playable cells to get.
     * @returns {Array<{r: number, c: number}>} An array of coordinates of random playable cells.
     */
    getRandomPlayableCells(numberOfCells) {
        const playableCells = [];
        for (let r = this.bufferSize; r < this.size - this.bufferSize; r++) {
            for (let c = this.bufferSize; c < this.size - this.bufferSize; c++) {
                playableCells.push({ r, c });
            }
        }

        // Shuffle the array (Fisher-Yates shuffle)
        for (let i = playableCells.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [playableCells[i], playableCells[j]] = [playableCells[j], playableCells[i]];
        }

        return playableCells.slice(0, numberOfCells);
    }


    /**
     * Gets the number of empty playable cells on the grid.
     * @returns {number} The count of empty cells.
     */
    getEmptyCellCount() {
        return this.emptyCells.size;
    }

    /**
     * Finds all empty neighboring cells (up, down, left, right) of a given cell.
     * @param {number} r - The row index of the center cell.
     * @param {number} c - The column index of the center cell.
     * @returns {Array<{r: number, c: number}>} An array of coordinates of empty neighbors.
     */
    findEmptyNeighbors(r, c) {
        const neighbors = [];
        const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]]; // Up, Down, Left, Right

        for (const [dr, dc] of directions) {
            const nr = r + dr;
            const nc = c + dc;
            if (this.isPlayable(nr, nc) && this.get(nr, nc) === null) {
                neighbors.push({ r: nr, c: nc });
            }
        }
        return neighbors;
    }

    /**
     * Finds a path between two points on the grid using Breadth-First Search.
     * NPCs can walk anywhere, so this implementation does not check for obstacles.
     * @param {{r: number, c: number}} start - The starting coordinates.
     * @param {{r: number, c: number}} end - The ending coordinates.
     * @param {import('../managers/ObstacleManager.js').ObstacleManager} [obstacleManager] - Optional manager for checking wall obstacles.
     * @returns {Array<{r: number, c: number}>|null} An array of coordinates representing the path, or null if no path is found.
     */
    findPath(start, end, obstacleManager) {
        const q = [[start]];
        const visited = new Set([`${start.r},${start.c}`]);

        while (q.length > 0) {
            const path = q.shift();
            const { r, c } = path[path.length - 1];

            if (r === end.r && c === end.c) {
                return path;
            }

            const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]];
            for (const [dr, dc] of directions) {
                const nr = r + dr;
                const nc = c + dc;
                const posKey = `${nr},${nc}`;

                // A cell is walkable if it's on the grid, not visited, and not an obstacle.
                const isWalkable = this.get(nr, nc) !== undefined && !visited.has(posKey);
                // Check if the edge between the current cell and the next is blocked.
                const isEdgeBlocked = obstacleManager ? obstacleManager.isEdgeBlocked(r, c, nr, nc) : false;

                if (isWalkable && !isEdgeBlocked) {
                    visited.add(posKey);
                    const newPath = [...path, { r: nr, c: nc }];
                    q.push(newPath);
                }
            }
        }
        return null; // No path found
    }

    /**
     * Populates the buffer zones of the grid with random buffer icons.
     * @param {string[]} bufferIcons - An array of string representations of buffer icons.
     */
    populateBuffer(bufferIcons) {
        if (!bufferIcons || bufferIcons.length === 0) return;

        for (let r = 0; r < this.size; r++) {
            for (let c = 0; c < this.size; c++) {
                if (!this.isPlayable(r, c)) {
                    const randomIcon = bufferIcons[Math.floor(Math.random() * bufferIcons.length)];
                    this.set(r, c, { display: randomIcon, isBuffer: true });
                }
            }
        }
    }

    /**
     * Places a specified number of random items onto random empty playable cells.
     * @param {Array<object>} items - An array of item objects to choose from.
     * @param {number} count - The number of items to place.
     */
    placeRandomItems(items, count) {
        if (!items || items.length === 0 || count === 0) return;

        for (let i = 0; i < count; i++) {
            const cell = this.getRandomEmptyCell();
            if (!cell) break; // Stop if no more empty cells
            const { r, c } = cell;
            const itemToPlace = items[Math.floor(Math.random() * items.length)];
            this.set(r, c, itemToPlace);
        }
    }

    /**
     * Clears a circular area on the grid by setting cells to null.
     * @param {number} row - The center row of the area to clear.
     * @param {number} col - The center column of the area to clear.
     * @param {number} radius - The radius of the area to clear.
     */
    clearArea(row, col, radius) {
        for (let r = row - radius; r <= row + radius; r++) {
            for (let c = col - radius; c <= col + radius; c++) {
                if (this.isPlayable(r, c)) {
                    this.set(r, c, null);
                }
            }
        }
    }
}